import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getSelectedPeTenantId,
  peCreate,
  peGet,
  peId,
  peLabel,
  peList,
  peRemove,
} from './peClient';
import { NeedTenant, PeNotice, PePage, PeTable, TabBar } from './PeShared';

export function PeRegulationsPage() {
  const tenantId = getSelectedPeTenantId();
  const [outerTab, setOuterTab] = useState('regulations');
  const [regulations, setRegulations] = useState([]);
  const [regulationId, setRegulationId] = useState('');
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState('');
  const [rows, setRows] = useState([]);
  const [innerTab, setInnerTab] = useState('cases');
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [divisions, setDivisions] = useState([]);

  const outerTabs = [
    { id: 'regulations', label: 'Regulations' },
    { id: 'detail', label: 'Cases & fields' },
    { id: 'payrolls', label: 'Payrolls' },
    { id: 'shares', label: 'Shared' },
  ];

  const innerTabs = [
    { id: 'cases', label: 'Cases' },
    { id: 'fields', label: 'Fields' },
    { id: 'relations', label: 'Case relations' },
    { id: 'wagetypes', label: 'Wage types' },
    { id: 'collectors', label: 'Collectors' },
    { id: 'lookups', label: 'Lookups' },
  ];

  async function loadRegulations() {
    if (!tenantId) return;
    const list = await peList(`tenants/${tenantId}/regulations`).catch(() => []);
    setRegulations(list);
    if (!regulationId && list[0]) setRegulationId(String(peId(list[0])));
    return list;
  }

  async function loadCases(regId = regulationId) {
    if (!tenantId || !regId) {
      setCases([]);
      return [];
    }
    const list = await peList(`tenants/${tenantId}/regulations/${regId}/cases`).catch(() => []);
    setCases(list);
    if (!caseId && list[0]) setCaseId(String(peId(list[0])));
    return list;
  }

  async function loadRows() {
    if (!tenantId) return;
    setBusy(true);
    setError('');
    try {
      if (outerTab === 'regulations') {
        setRows(await peList(`tenants/${tenantId}/regulations`));
      } else if (outerTab === 'payrolls') {
        setRows(await peList(`tenants/${tenantId}/payrolls`));
      } else if (outerTab === 'shares') {
        setRows(await peList('shares/regulations'));
      } else if (outerTab === 'detail' && regulationId) {
        if (innerTab === 'cases') {
          const list = await loadCases(regulationId);
          setRows(list);
        } else if (innerTab === 'fields') {
          if (!caseId) {
            setRows([]);
          } else {
            setRows(await peList(`tenants/${tenantId}/regulations/${regulationId}/cases/${caseId}/fields`));
          }
        } else if (innerTab === 'relations') {
          setRows(await peList(`tenants/${tenantId}/regulations/${regulationId}/caserelations`));
        } else if (innerTab === 'wagetypes') {
          setRows(await peList(`tenants/${tenantId}/regulations/${regulationId}/wagetypes`));
        } else if (innerTab === 'collectors') {
          setRows(await peList(`tenants/${tenantId}/regulations/${regulationId}/collectors`));
        } else if (innerTab === 'lookups') {
          setRows(await peList(`tenants/${tenantId}/regulations/${regulationId}/lookups`));
        }
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(err.message || 'Could not load regulations data.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!tenantId) return;
    loadRegulations().catch(() => {});
    peList(`tenants/${tenantId}/divisions`).then(setDivisions).catch(() => setDivisions([]));
  }, [tenantId]);

  useEffect(() => {
    if (regulationId) loadCases(regulationId).catch(() => {});
  }, [tenantId, regulationId]);

  useEffect(() => { loadRows(); }, [tenantId, outerTab, innerTab, regulationId, caseId]);

  if (!tenantId) {
    return (
      <PePage title="Regulations" lede="Regulation layers, cases, fields, and payrolls.">
        <NeedTenant />
      </PePage>
    );
  }

  async function onCreate(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (outerTab === 'regulations') {
        await peCreate(`tenants/${tenantId}/regulations`, {
          name: (draft.name || '').trim(),
          version: Number(draft.version) || 1,
        });
        setMessage('Regulation created.');
        setDraft({});
        await loadRegulations();
        await loadRows();
      } else if (outerTab === 'payrolls') {
        await peCreate(`tenants/${tenantId}/payrolls`, {
          name: (draft.name || '').trim(),
          divisionName: (draft.divisionName || '').trim() || undefined,
        });
        setMessage('Payroll created.');
        setDraft({});
        await loadRows();
      } else if (outerTab === 'detail' && regulationId) {
        if (innerTab === 'cases') {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/cases`, {
            name: (draft.name || '').trim(),
            caseType: draft.caseType || 'Employee',
            description: (draft.description || '').trim() || undefined,
          });
          setMessage('Case created.');
          setDraft({ caseType: 'Employee' });
          await loadCases(regulationId);
          await loadRows();
        } else if (innerTab === 'fields' && caseId) {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/cases/${caseId}/fields`, {
            name: (draft.name || '').trim(),
            valueType: draft.valueType || 'Decimal',
            timeType: draft.timeType || 'Period',
          });
          setMessage('Field created.');
          setDraft({ valueType: 'Decimal', timeType: 'Period' });
          await loadRows();
        } else if (innerTab === 'relations') {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/caserelations`, {
            sourceCaseName: (draft.sourceCaseName || '').trim(),
            targetCaseName: (draft.targetCaseName || '').trim(),
          });
          setMessage('Case relation created.');
          setDraft({});
          await loadRows();
        } else if (innerTab === 'wagetypes') {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/wagetypes`, {
            name: (draft.name || '').trim(),
            wageTypeNumber: Number(draft.wageTypeNumber) || undefined,
            description: (draft.description || '').trim() || undefined,
          });
          setMessage('Wage type created.');
          setDraft({});
          await loadRows();
        } else if (innerTab === 'collectors') {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/collectors`, {
            name: (draft.name || '').trim(),
            collectorType: draft.collectorType || 'Collector',
            description: (draft.description || '').trim() || undefined,
          });
          setMessage('Collector created.');
          setDraft({});
          await loadRows();
        } else if (innerTab === 'lookups') {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/lookups`, {
            name: (draft.name || '').trim(),
          });
          setMessage('Lookup created.');
          setDraft({});
          await loadRows();
        }
      }
    } catch (err) {
      setError(err.message || 'Create failed.');
      setBusy(false);
    }
  }

  async function onDelete(row) {
    const id = peId(row);
    if (!id || !window.confirm(`Delete ${peLabel(row)}?`)) return;
    setBusy(true);
    setError('');
    try {
      let path = null;
      if (outerTab === 'regulations') path = `tenants/${tenantId}/regulations/${id}`;
      else if (outerTab === 'payrolls') path = `tenants/${tenantId}/payrolls/${id}`;
      else if (outerTab === 'detail' && regulationId) {
        if (innerTab === 'cases') path = `tenants/${tenantId}/regulations/${regulationId}/cases/${id}`;
        else if (innerTab === 'fields' && caseId) {
          path = `tenants/${tenantId}/regulations/${regulationId}/cases/${caseId}/fields/${id}`;
        } else if (innerTab === 'relations') {
          path = `tenants/${tenantId}/regulations/${regulationId}/caserelations/${id}`;
        } else if (innerTab === 'wagetypes') {
          path = `tenants/${tenantId}/regulations/${regulationId}/wagetypes/${id}`;
        } else if (innerTab === 'collectors') {
          path = `tenants/${tenantId}/regulations/${regulationId}/collectors/${id}`;
        } else if (innerTab === 'lookups') {
          path = `tenants/${tenantId}/regulations/${regulationId}/lookups/${id}`;
        }
      }
      if (!path) return;
      await peRemove(path);
      if (innerTab === 'cases') await loadCases(regulationId);
      await loadRows();
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  }

  async function rebuildCase(row) {
    const id = peId(row);
    if (!id || !regulationId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await peCreate(`tenants/${tenantId}/regulations/${regulationId}/cases/${id}/rebuild`, {});
      setMessage(`Rebuilt case ${peLabel(row)}.`);
      await loadRows();
    } catch (err) {
      setError(err.message || 'Rebuild failed.');
      setBusy(false);
    }
  }

  async function bootstrapRebelityCases() {
    if (!regulationId) {
      setError('Select a regulation first.');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const existing = await loadCases(regulationId);
      const names = new Set(existing.map((c) => String(c.name || c.Name || '').toLowerCase()));

      async function ensureCase(name, description, fieldName) {
        let target = existing.find((c) => String(c.name || c.Name || '').toLowerCase() === name.toLowerCase());
        if (!target) {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/cases`, {
            name,
            caseType: 'Employee',
            description,
          });
          const refreshed = await loadCases(regulationId);
          target = refreshed.find((c) => String(c.name || c.Name || '').toLowerCase() === name.toLowerCase());
        }
        if (!target) return;
        const cid = peId(target);
        const fields = await peList(`tenants/${tenantId}/regulations/${regulationId}/cases/${cid}/fields`).catch(() => []);
        const hasField = fields.some((f) => String(f.name || f.Name || '').toLowerCase() === fieldName.toLowerCase());
        if (!hasField) {
          await peCreate(`tenants/${tenantId}/regulations/${regulationId}/cases/${cid}/fields`, {
            name: fieldName,
            valueType: 'Decimal',
            timeType: 'Period',
          });
        }
      }

      await ensureCase('WageHours', 'Hours worked in pay period', 'Hours');
      await ensureCase('TipAmount', 'Unpaid tips and gratuity for pay period', 'Amount');
      setMessage(names.has('wagehours') && names.has('tipamount')
        ? 'Rebelity WageHours / TipAmount cases verified (fields ensured).'
        : 'Rebelity WageHours / TipAmount cases and fields created.');
      setInnerTab('cases');
      await loadRows();
    } catch (err) {
      setError(err.message || 'Bootstrap failed.');
      setBusy(false);
    }
  }

  function columnsFor() {
    if (outerTab === 'regulations') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'version', alt: 'Version', label: 'Version' },
      ];
    }
    if (outerTab === 'payrolls') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'divisionName', alt: 'DivisionName', label: 'Division' },
      ];
    }
    if (outerTab === 'shares') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
      ];
    }
    if (innerTab === 'cases') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'caseType', alt: 'CaseType', label: 'Type' },
        { key: 'description', alt: 'Description', label: 'Description' },
      ];
    }
    if (innerTab === 'fields') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'valueType', alt: 'ValueType', label: 'Value type' },
        { key: 'timeType', alt: 'TimeType', label: 'Time type' },
      ];
    }
    if (innerTab === 'relations') {
      return [
        { key: 'sourceCaseName', alt: 'SourceCaseName', label: 'Source' },
        { key: 'targetCaseName', alt: 'TargetCaseName', label: 'Target' },
      ];
    }
    if (innerTab === 'wagetypes') {
      return [
        { key: 'name', alt: 'Name', label: 'Name' },
        { key: 'wageTypeNumber', alt: 'WageTypeNumber', label: 'Number' },
        { key: 'description', alt: 'Description', label: 'Description' },
      ];
    }
    return [
      { key: 'name', alt: 'Name', label: 'Name' },
      { key: 'description', alt: 'Description', label: 'Description' },
    ];
  }

  return (
    <PePage
      title="Regulations"
      lede="Regulation layers plus case/field depth for the selected PE tenant. Use Cases & fields to define WageHours, TipAmount, and other PE cases."
    >
      <p className="muted">
        Tenant {tenantId} · <Link to="/app/tenants">Change</Link>
      </p>
      <PeNotice error={error} busy={busy} message={message} />
      <TabBar tabs={outerTabs} active={outerTab} onChange={(id) => {
        setOuterTab(id);
        setDraft({});
        setMessage('');
      }}
      />

      {outerTab === 'detail' ? (
        <div className="pay-toolbar">
          <label>
            Regulation
            <select
              value={regulationId}
              onChange={(e) => {
                setRegulationId(e.target.value);
                setCaseId('');
                setDraft({});
              }}
            >
              <option value="">Select regulation</option>
              {regulations.map((reg) => (
                <option key={peId(reg)} value={peId(reg)}>
                  {peLabel(reg)}{reg.version || reg.Version ? ` v${reg.version || reg.Version}` : ''}
                </option>
              ))}
            </select>
          </label>
          {innerTab === 'fields' ? (
            <label>
              Case
              <select value={caseId} onChange={(e) => { setCaseId(e.target.value); setDraft({ valueType: 'Decimal', timeType: 'Period' }); }}>
                <option value="">Select case</option>
                {cases.map((c) => (
                  <option key={peId(c)} value={peId(c)}>{peLabel(c)}</option>
                ))}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy || !regulationId}
            onClick={bootstrapRebelityCases}
          >
            Ensure WageHours + TipAmount
          </button>
        </div>
      ) : null}

      {outerTab === 'detail' ? (
        <TabBar tabs={innerTabs} active={innerTab} onChange={(id) => { setInnerTab(id); setDraft({}); setMessage(''); }} />
      ) : null}

      <section className="panel">
        <h2>
          {outerTab === 'detail'
            ? (innerTabs.find((t) => t.id === innerTab)?.label || 'Detail')
            : (outerTabs.find((t) => t.id === outerTab)?.label || 'Regulations')}
        </h2>
        {outerTab === 'detail' && !regulationId ? (
          <p className="muted">Select a regulation to manage cases and fields.</p>
        ) : outerTab === 'detail' && innerTab === 'fields' && !caseId ? (
          <p className="muted">Select a case to manage its fields.</p>
        ) : (
          <PeTable
            columns={columnsFor()}
            rows={rows}
            empty="Nothing returned from Payroll Engine."
            onDelete={outerTab === 'shares' ? undefined : onDelete}
            actions={outerTab === 'detail' && innerTab === 'cases' ? [
              { id: 'rebuild', label: 'Rebuild', onClick: rebuildCase },
              {
                id: 'fields',
                label: 'Fields',
                onClick: (row) => {
                  setCaseId(String(peId(row)));
                  setInnerTab('fields');
                  setDraft({ valueType: 'Decimal', timeType: 'Period' });
                },
              },
            ] : undefined}
          />
        )}
      </section>

      {outerTab === 'shares' ? null : (
        <section className="panel">
          <h2>
            {outerTab === 'regulations' && 'Add regulation'}
            {outerTab === 'payrolls' && 'Add payroll'}
            {outerTab === 'detail' && innerTab === 'cases' && 'Add case'}
            {outerTab === 'detail' && innerTab === 'fields' && 'Add field'}
            {outerTab === 'detail' && innerTab === 'relations' && 'Add case relation'}
            {outerTab === 'detail' && innerTab === 'wagetypes' && 'Add wage type'}
            {outerTab === 'detail' && innerTab === 'collectors' && 'Add collector'}
            {outerTab === 'detail' && innerTab === 'lookups' && 'Add lookup'}
          </h2>
          {outerTab === 'detail' && !regulationId ? (
            <p className="muted">Select a regulation first.</p>
          ) : outerTab === 'detail' && innerTab === 'fields' && !caseId ? (
            <p className="muted">Select a case first.</p>
          ) : (
            <form className="form form-wide" onSubmit={onCreate}>
              {outerTab === 'regulations' ? (
                <>
                  <label>
                    Name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </label>
                  <label>
                    Version
                    <input type="number" value={draft.version || '1'} onChange={(e) => setDraft({ ...draft, version: e.target.value })} />
                  </label>
                </>
              ) : null}
              {outerTab === 'payrolls' ? (
                <>
                  <label>
                    Name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </label>
                  <label>
                    Division
                    <select value={draft.divisionName || ''} onChange={(e) => setDraft({ ...draft, divisionName: e.target.value })}>
                      <option value="">Optional</option>
                      {divisions.map((div) => {
                        const name = peLabel(div);
                        return <option key={peId(div) || name} value={name}>{name}</option>;
                      })}
                    </select>
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'cases' ? (
                <>
                  <label>
                    Name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="WageHours" list="pe-reg-case-names" />
                    <datalist id="pe-reg-case-names">
                      <option value="WageHours" />
                      <option value="TipAmount" />
                    </datalist>
                  </label>
                  <label>
                    Case type
                    <select value={draft.caseType || 'Employee'} onChange={(e) => setDraft({ ...draft, caseType: e.target.value })}>
                      <option value="Employee">Employee</option>
                      <option value="Company">Company</option>
                      <option value="National">National</option>
                      <option value="Global">Global</option>
                    </select>
                  </label>
                  <label>
                    Description
                    <input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'fields' ? (
                <>
                  <label>
                    Field name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Hours" list="pe-reg-field-names" />
                    <datalist id="pe-reg-field-names">
                      <option value="Hours" />
                      <option value="Amount" />
                    </datalist>
                  </label>
                  <label>
                    Value type
                    <select value={draft.valueType || 'Decimal'} onChange={(e) => setDraft({ ...draft, valueType: e.target.value })}>
                      <option value="Decimal">Decimal</option>
                      <option value="Integer">Integer</option>
                      <option value="String">String</option>
                      <option value="Boolean">Boolean</option>
                      <option value="DateTime">DateTime</option>
                      <option value="Money">Money</option>
                    </select>
                  </label>
                  <label>
                    Time type
                    <select value={draft.timeType || 'Period'} onChange={(e) => setDraft({ ...draft, timeType: e.target.value })}>
                      <option value="Period">Period</option>
                      <option value="Calendar">Calendar</option>
                      <option value="Timeless">Timeless</option>
                    </select>
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'relations' ? (
                <>
                  <label>
                    Source case
                    <select required value={draft.sourceCaseName || ''} onChange={(e) => setDraft({ ...draft, sourceCaseName: e.target.value })}>
                      <option value="">Select</option>
                      {cases.map((c) => <option key={peId(c)} value={peLabel(c)}>{peLabel(c)}</option>)}
                    </select>
                  </label>
                  <label>
                    Target case
                    <select required value={draft.targetCaseName || ''} onChange={(e) => setDraft({ ...draft, targetCaseName: e.target.value })}>
                      <option value="">Select</option>
                      {cases.map((c) => <option key={peId(c)} value={peLabel(c)}>{peLabel(c)}</option>)}
                    </select>
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'wagetypes' ? (
                <>
                  <label>
                    Name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </label>
                  <label>
                    Wage type number
                    <input type="number" value={draft.wageTypeNumber || ''} onChange={(e) => setDraft({ ...draft, wageTypeNumber: e.target.value })} />
                  </label>
                  <label>
                    Description
                    <input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'collectors' ? (
                <>
                  <label>
                    Name
                    <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </label>
                  <label>
                    Collector type
                    <input value={draft.collectorType || 'Collector'} onChange={(e) => setDraft({ ...draft, collectorType: e.target.value })} />
                  </label>
                  <label>
                    Description
                    <input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                  </label>
                </>
              ) : null}
              {outerTab === 'detail' && innerTab === 'lookups' ? (
                <label>
                  Name
                  <input required value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </label>
              ) : null}
              <button className="btn btn-primary" type="submit" disabled={busy}>Add</button>
            </form>
          )}
        </section>
      )}
    </PePage>
  );
}


const CASE_SCOPE_TABS = [
  { id: 'employee', label: 'Employee cases' },
  { id: 'company', label: 'Company' },
  { id: 'national', label: 'National' },
  { id: 'global', label: 'Global' },
];

function caseListPath(scope, tenantId, employeeId) {
  if (scope === 'employee') {
    return employeeId ? `tenants/${tenantId}/employees/${employeeId}/cases` : null;
  }
  if (scope === 'company') return `tenants/${tenantId}/companycases`;
  if (scope === 'national') return `tenants/${tenantId}/nationalcases`;
  return `tenants/${tenantId}/globalcases`;
}

function caseChangePath(scope, tenantId, employeeId) {
  if (scope === 'employee') {
    return employeeId ? `tenants/${tenantId}/employees/${employeeId}/cases/changes` : null;
  }
  if (scope === 'company') return `tenants/${tenantId}/companycases/changes`;
  if (scope === 'national') return `tenants/${tenantId}/nationalcases/changes`;
  return `tenants/${tenantId}/globalcases/changes`;
}

function emptyCaseDraft() {
  return {
    caseName: 'WageHours',
    caseFieldName: 'Hours',
    value: '',
    start: '',
    end: '',
    reason: 'Rebelity Payroll case change',
    divisionName: '',
  };
}

export function PeCasesPage() {
  const tenantId = getSelectedPeTenantId();
  const [scope, setScope] = useState('employee');
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [userIdentifier, setUserIdentifier] = useState('');
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(emptyCaseDraft());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    Promise.all([
      peList(`tenants/${tenantId}/employees`).catch(() => []),
      peList(`tenants/${tenantId}/users`).catch(() => []),
      peList(`tenants/${tenantId}/divisions`).catch(() => []),
    ]).then(([emps, peUsers, divs]) => {
      setEmployees(emps);
      setUsers(peUsers);
      setDivisions(divs);
      if (!employeeId && emps[0]) setEmployeeId(String(peId(emps[0])));
      if (!userIdentifier && peUsers[0]) {
        setUserIdentifier(String(peUsers[0].identifier || peUsers[0].Identifier || ''));
      }
      if (!draft.divisionName && divs[0]) {
        setDraft((prev) => ({ ...prev, divisionName: peLabel(divs[0]) }));
      }
    });
  }, [tenantId]);

  async function loadCases() {
    if (!tenantId) return;
    const path = caseListPath(scope, tenantId, employeeId);
    if (!path) {
      setRows([]);
      return;
    }
    setBusy(true);
    setError('');
    try {
      setRows(await peList(path));
    } catch (err) {
      setError(err.message || 'Could not load case values.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { loadCases(); }, [tenantId, scope, employeeId]);

  if (!tenantId) {
    return (
      <PePage title="Case data" lede="Employee, company, national, and global case values.">
        <NeedTenant />
      </PePage>
    );
  }

  async function onCreateChange(event) {
    event.preventDefault();
    const changePath = caseChangePath(scope, tenantId, employeeId);
    if (!changePath) {
      setError('Select an employee before adding an employee case change.');
      return;
    }
    const caseName = (draft.caseName || '').trim();
    const caseFieldName = (draft.caseFieldName || '').trim();
    const value = (draft.value || '').trim();
    if (!caseName || !caseFieldName || !value) {
      setError('Case name, field name, and value are required.');
      return;
    }

    const selectedEmployee = employees.find((emp) => String(peId(emp)) === String(employeeId));
    const employeeIdentifier = selectedEmployee?.identifier || selectedEmployee?.Identifier || '';
    const valueEntry = {
      caseFieldName,
      value,
    };
    if (draft.start) valueEntry.start = new Date(draft.start).toISOString();
    if (draft.end) valueEntry.end = new Date(draft.end).toISOString();

    const payload = {
      reason: (draft.reason || '').trim() || 'Rebelity Payroll case change',
      case: {
        caseName,
        values: [valueEntry],
      },
    };
    if (userIdentifier) payload.userIdentifier = userIdentifier;
    if (scope === 'employee' && employeeIdentifier) payload.employeeIdentifier = employeeIdentifier;
    if (draft.divisionName) payload.divisionName = draft.divisionName;

    setBusy(true);
    setError('');
    setMessage('');
    try {
      await peCreate(changePath, payload);
      setMessage(`Case change saved (${caseName}.${caseFieldName}).`);
      setDraft((prev) => ({
        ...emptyCaseDraft(),
        divisionName: prev.divisionName,
        caseName: prev.caseName,
        caseFieldName: prev.caseFieldName,
      }));
      await loadCases();
    } catch (err) {
      setError(err.message || 'Case change failed.');
      setBusy(false);
    }
  }

  async function onDelete(row) {
    const id = peId(row);
    const path = caseListPath(scope, tenantId, employeeId);
    if (!id || !path || !window.confirm(`Delete case value ${peLabel(row)}?`)) return;
    setBusy(true);
    setError('');
    try {
      await peRemove(`${path}/${id}`);
      await loadCases();
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  }

  return (
    <PePage
      title="Case data"
      lede="Add and review PE case values in the current Rebelity UI. Hours/tips from Pay period sync here; you can also enter changes natively."
    >
      <p className="muted">
        Tenant {tenantId} · <Link to="/app/tenants">Change</Link>
      </p>
      <PeNotice error={error} busy={busy} message={message} />
      <div className="pay-toolbar">
        <label>
          PE user (for case change)
          <select value={userIdentifier} onChange={(e) => setUserIdentifier(e.target.value)}>
            <option value="">Optional</option>
            {users.map((user) => {
              const idf = user.identifier || user.Identifier || '';
              return <option key={peId(user) || idf} value={idf}>{peLabel(user)} ({idf})</option>;
            })}
          </select>
        </label>
        {scope === 'employee' ? (
          <label>
            Employee
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={peId(emp)} value={peId(emp)}>{peLabel(emp)}</option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <TabBar tabs={CASE_SCOPE_TABS} active={scope} onChange={setScope} />

      <section className="panel">
        <h2>Add case change</h2>
        <p className="muted">
          Posts to PE <code>.../cases/changes</code>. Common Rebelity fields: WageHours / Hours, TipAmount / Amount.
        </p>
        <form className="form form-wide" onSubmit={onCreateChange}>
          <label>
            Case name
            <input
              required
              value={draft.caseName}
              onChange={(e) => setDraft({ ...draft, caseName: e.target.value })}
              placeholder="WageHours"
              list="pe-case-names"
            />
            <datalist id="pe-case-names">
              <option value="WageHours" />
              <option value="TipAmount" />
            </datalist>
          </label>
          <label>
            Field name
            <input
              required
              value={draft.caseFieldName}
              onChange={(e) => setDraft({ ...draft, caseFieldName: e.target.value })}
              placeholder="Hours"
              list="pe-case-fields"
            />
            <datalist id="pe-case-fields">
              <option value="Hours" />
              <option value="Amount" />
            </datalist>
          </label>
          <label>
            Value
            <input
              required
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder="40"
            />
          </label>
          <label>
            Start (optional)
            <input
              type="datetime-local"
              value={draft.start}
              onChange={(e) => setDraft({ ...draft, start: e.target.value })}
            />
          </label>
          <label>
            End (optional)
            <input
              type="datetime-local"
              value={draft.end}
              onChange={(e) => setDraft({ ...draft, end: e.target.value })}
            />
          </label>
          <label>
            Division
            <select
              value={draft.divisionName}
              onChange={(e) => setDraft({ ...draft, divisionName: e.target.value })}
            >
              <option value="">None</option>
              {divisions.map((div) => {
                const name = peLabel(div);
                return <option key={peId(div) || name} value={name}>{name}</option>;
              })}
            </select>
          </label>
          <label>
            Reason
            <input
              value={draft.reason}
              onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            />
          </label>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={busy || (scope === 'employee' && !employeeId)}
          >
            Save case change
          </button>
        </form>
      </section>

      <section className="panel">
        <h2>{CASE_SCOPE_TABS.find((t) => t.id === scope)?.label}</h2>
        {scope === 'employee' && !employeeId ? (
          <p className="muted">Select an employee to list case values.</p>
        ) : (
          <PeTable
            columns={caseColumns()}
            rows={rows}
            empty="No case values yet. Add a case change above."
            onDelete={onDelete}
          />
        )}
      </section>
    </PePage>
  );
}

export function PeReportsPage() {
  const tenantId = getSelectedPeTenantId();
  const [regulations, setRegulations] = useState([]);
  const [regulationId, setRegulationId] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    peList(`tenants/${tenantId}/regulations`).then((list) => {
      setRegulations(list);
      if (!regulationId && list[0]) setRegulationId(String(peId(list[0])));
    }).catch(() => setRegulations([]));
  }, [tenantId]);

  return (
    <PeCollectionPage
      title="Reports"
      lede="Regulation reports, payroll reports, and report execution logs."
      reloadKey={regulationId}
      extra={tenantId ? (
        <label>
          Regulation
          <select value={regulationId} onChange={(e) => setRegulationId(e.target.value)}>
            <option value="">Select regulation</option>
            {regulations.map((reg) => (
              <option key={peId(reg)} value={peId(reg)}>{peLabel(reg)}</option>
            ))}
          </select>
        </label>
      ) : null}
      tabs={[
        {
          id: 'reg-reports',
          label: 'Regulation reports',
          path: (tid) => (regulationId ? `tenants/${tid}/regulations/${regulationId}/reports` : null),
          columns: [
            { key: 'name', alt: 'Name', label: 'Name' },
            { key: 'description', alt: 'Description', label: 'Description' },
          ],
        },
        {
          id: 'logs',
          label: 'Report logs',
          path: (tid) => `tenants/${tid}/reportlogs`,
          columns: [
            { key: 'name', alt: 'Name', label: 'Name' },
            { key: 'message', alt: 'Message', label: 'Message' },
            { key: 'created', alt: 'Created', label: 'Created' },
          ],
        },
      ]}
    />
  );
}

export function PeTasksPage() {
  return (
    <PeCollectionPage
      title="Tasks"
      lede="User tasks stored on the Payroll Engine tenant."
      tabs={[
        {
          id: 'tasks',
          label: 'Tasks',
          path: (tid) => `tenants/${tid}/tasks`,
          columns: [
            { key: 'name', alt: 'Name', label: 'Name' },
            { key: 'instruction', alt: 'Instruction', label: 'Instruction' },
            { key: 'scheduled', alt: 'Scheduled', label: 'Scheduled' },
          ],
          fields: [
            { key: 'name', label: 'Name', required: true },
            { key: 'instruction', label: 'Instruction' },
          ],
          toPayload: (d) => ({ name: d.name, instruction: d.instruction || '' }),
        },
      ]}
    />
  );
}

export function PeLogsPage() {
  return (
    <PeCollectionPage
      title="Developer logs"
      lede="Tenant logs from the private Payroll Engine host."
      tabs={[
        {
          id: 'logs',
          label: 'Logs',
          path: (tid) => `tenants/${tid}/logs`,
          columns: [
            { key: 'level', alt: 'Level', label: 'Level' },
            { key: 'message', alt: 'Message', label: 'Message' },
            { key: 'created', alt: 'Created', label: 'Created' },
          ],
        },
      ]}
    />
  );
}

export function PePayrunsPage() {
  const tenantId = getSelectedPeTenantId();
  const [tab, setTab] = useState('jobs');
  const [rows, setRows] = useState([]);
  const [payruns, setPayruns] = useState([]);
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState({
    name: '',
    identifier: '',
    payrunName: '',
    periodStart: '',
    periodEnd: '',
    userIdentifier: '',
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const tabs = [
    { id: 'payruns', label: 'Payruns' },
    { id: 'jobs', label: 'Jobs' },
    { id: 'results', label: 'Payroll results' },
  ];

  async function load() {
    if (!tenantId) return;
    setBusy(true);
    setError('');
    try {
      const [payrunList, userList] = await Promise.all([
        peList(`tenants/${tenantId}/payruns`).catch(() => []),
        peList(`tenants/${tenantId}/users`).catch(() => []),
      ]);
      setPayruns(payrunList);
      setUsers(userList);
      setDraft((prev) => ({
        ...prev,
        payrunName: prev.payrunName || (payrunList[0] ? peLabel(payrunList[0]) : ''),
        userIdentifier: prev.userIdentifier || (userList[0]?.identifier || userList[0]?.Identifier || ''),
      }));

      if (tab === 'payruns') setRows(payrunList);
      else if (tab === 'jobs') setRows(await peList(`tenants/${tenantId}/payruns/jobs`));
      else setRows(await peList(`tenants/${tenantId}/payrollresults`));
    } catch (err) {
      setError(err.message || 'Could not load PE payrun data.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, [tenantId, tab]);

  if (!tenantId) {
    return (
      <PePage title="PE payruns & jobs" lede="Payroll Engine payrun definitions and jobs.">
        <NeedTenant />
      </PePage>
    );
  }

  async function onCreatePayrun(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await peCreate(`tenants/${tenantId}/payruns`, {
        name: (draft.name || '').trim(),
        identifier: (draft.identifier || '').trim() || undefined,
      });
      setMessage('Payrun created.');
      setDraft((prev) => ({ ...prev, name: '', identifier: '' }));
      setTab('payruns');
      await load();
    } catch (err) {
      setError(err.message || 'Create payrun failed.');
      setBusy(false);
    }
  }

  async function onCreateJob(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    setPreview(null);
    try {
      const payload = {
        name: (draft.name || '').trim(),
        payrunName: (draft.payrunName || '').trim(),
        periodStart: new Date(draft.periodStart).toISOString(),
        periodEnd: new Date(draft.periodEnd).toISOString(),
      };
      if (draft.userIdentifier) payload.userIdentifier = draft.userIdentifier;
      await peCreate(`tenants/${tenantId}/payruns/jobs`, payload);
      setMessage('Payrun job created.');
      setDraft((prev) => ({ ...prev, name: '' }));
      setTab('jobs');
      await load();
    } catch (err) {
      setError(err.message || 'Create job failed.');
      setBusy(false);
    }
  }

  async function refreshStatus(row) {
    const id = peId(row);
    if (!id) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const status = await peGet(`tenants/${tenantId}/payruns/jobs/${id}/status`);
      const label = typeof status === 'string'
        ? status
        : (status?.status ?? status?.Status ?? status?.jobStatus ?? JSON.stringify(status));
      setMessage(`Job ${peLabel(row)} status: ${label}`);
      await load();
    } catch (err) {
      setError(err.message || 'Status refresh failed.');
      setBusy(false);
    }
  }

  async function runPreview() {
    setBusy(true);
    setError('');
    setMessage('');
    setPreview(null);
    try {
      const payload = {
        name: (draft.name || 'Preview').trim(),
        payrunName: (draft.payrunName || '').trim(),
        periodStart: new Date(draft.periodStart).toISOString(),
        periodEnd: new Date(draft.periodEnd).toISOString(),
      };
      if (draft.userIdentifier) payload.userIdentifier = draft.userIdentifier;
      const data = await peCreate(`tenants/${tenantId}/payruns/jobs/preview`, payload);
      setPreview(data);
      setMessage('Preview returned from Payroll Engine.');
    } catch (err) {
      setError(err.message || 'Preview failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PePage
      title="PE payruns & jobs"
      lede="Payroll Engine payrun definitions, job runs, status, and preview. Workspace Payrun remains the Rebelity-facing flow."
    >
      <p className="muted">
        Tenant {tenantId} · <Link to="/app/tenants">Change</Link>
      </p>
      <PeNotice error={error} busy={busy} message={message} />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'jobs' ? (
        <section className="panel">
          <h2>Create / preview job</h2>
          <form className="form form-wide" onSubmit={onCreateJob}>
            <label>
              Job name
              <input
                required
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="PayrunJob.2026-08"
              />
            </label>
            <label>
              Payrun
              <select
                required
                value={draft.payrunName}
                onChange={(e) => setDraft({ ...draft, payrunName: e.target.value })}
              >
                <option value="">Select payrun</option>
                {payruns.map((pr) => (
                  <option key={peId(pr) || peLabel(pr)} value={peLabel(pr)}>{peLabel(pr)}</option>
                ))}
              </select>
            </label>
            <label>
              Period start
              <input
                type="date"
                required
                value={draft.periodStart}
                onChange={(e) => setDraft({ ...draft, periodStart: e.target.value })}
              />
            </label>
            <label>
              Period end
              <input
                type="date"
                required
                value={draft.periodEnd}
                onChange={(e) => setDraft({ ...draft, periodEnd: e.target.value })}
              />
            </label>
            <label>
              PE user
              <select
                value={draft.userIdentifier}
                onChange={(e) => setDraft({ ...draft, userIdentifier: e.target.value })}
              >
                <option value="">Optional</option>
                {users.map((user) => {
                  const idf = user.identifier || user.Identifier || '';
                  return <option key={peId(user) || idf} value={idf}>{peLabel(user)} ({idf})</option>;
                })}
              </select>
            </label>
            <div className="pay-toolbar">
              <button className="btn btn-primary" type="submit" disabled={busy || !payruns.length}>
                Create job
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                disabled={busy || !draft.payrunName || !draft.periodStart || !draft.periodEnd}
                onClick={runPreview}
              >
                Preview
              </button>
            </div>
            {!payruns.length ? (
              <p className="muted">Create a payrun on the Payruns tab first.</p>
            ) : null}
          </form>
          {preview ? (
            <pre className="muted" style={{ marginTop: 16, whiteSpace: 'pre-wrap', maxHeight: 280, overflow: 'auto' }}>
              {JSON.stringify(preview, null, 2)}
            </pre>
          ) : null}
        </section>
      ) : null}

      {tab === 'payruns' ? (
        <section className="panel">
          <h2>Add payrun</h2>
          <form className="form form-wide" onSubmit={onCreatePayrun}>
            <label>
              Name
              <input
                required
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              Identifier
              <input
                value={draft.identifier || ''}
                onChange={(e) => setDraft({ ...draft, identifier: e.target.value })}
              />
            </label>
            <button className="btn btn-primary" type="submit" disabled={busy}>Add payrun</button>
          </form>
        </section>
      ) : null}

      <section className="panel">
        <h2>{tabs.find((t) => t.id === tab)?.label}</h2>
        <PeTable
          columns={
            tab === 'jobs'
              ? [
                  { key: 'name', alt: 'Name', label: 'Name' },
                  { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
                  {
                    key: 'status',
                    label: 'Status',
                    render: (row) => String(row.status ?? row.Status ?? row.jobStatus ?? row.JobStatus ?? '—'),
                  },
                  {
                    key: 'period',
                    label: 'Period',
                    render: (row) => {
                      const start = row.periodStart ?? row.PeriodStart ?? row.startDate ?? row.StartDate;
                      const end = row.periodEnd ?? row.PeriodEnd ?? row.endDate ?? row.EndDate;
                      if (!start && !end) return '—';
                      return `${start || '?'} → ${end || '?'}`;
                    },
                  },
                ]
              : [
                  { key: 'name', alt: 'Name', label: 'Name' },
                  { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
                  {
                    key: 'created',
                    label: 'Created',
                    render: (row) => String(row.created ?? row.Created ?? row.createdDate ?? row.CreatedDate ?? '—'),
                  },
                ]
          }
          rows={rows}
          empty="Nothing returned from Payroll Engine."
          actions={tab === 'jobs' ? [
            { id: 'status', label: 'Status', onClick: refreshStatus },
          ] : undefined}
        />
      </section>
    </PePage>
  );
}

function caseColumns() {
  return [
    { key: 'caseName', alt: 'CaseName', label: 'Case' },
    { key: 'name', alt: 'Name', label: 'Name' },
    {
      key: 'value',
      alt: 'Value',
      label: 'Value',
      render: (row) => String(row.value ?? row.Value ?? row.caseValue ?? row.CaseValue ?? '—'),
    },
  ];
}

function PeCollectionPage({ title, lede, tabs, extra, reloadKey }) {
  const tenantId = getSelectedPeTenantId();
  const [tab, setTab] = useState(tabs[0]?.id);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState({});
  const active = tabs.find((t) => t.id === tab) || tabs[0];

  async function load(next = active) {
    if (!tenantId) return;
    const path = next.path(tenantId);
    if (!path) {
      setRows([]);
      return;
    }
    setBusy(true);
    setError('');
    try {
      setRows(await peList(path));
    } catch (err) {
      setError(err.message || 'Could not load engine data.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(active); }, [tenantId, tab, reloadKey]);

  if (!tenantId) {
    return (
      <PePage title={title} lede={lede}>
        <NeedTenant />
      </PePage>
    );
  }

  async function onCreate(event) {
    event.preventDefault();
    if (!active.toPayload) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await peCreate(active.path(tenantId), active.toPayload(draft));
      setDraft({});
      setMessage('Saved.');
      await load(active);
    } catch (err) {
      setError(err.message || 'Create failed.');
      setBusy(false);
    }
  }

  async function onDelete(row) {
    const id = peId(row);
    const path = active.path(tenantId);
    if (!id || !path || !window.confirm(`Delete ${peLabel(row)}?`)) return;
    setBusy(true);
    setError('');
    try {
      await peRemove(`${path}/${id}`);
      await load(active);
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  }

  return (
    <PePage title={title} lede={lede}>
      <p className="muted">
        Tenant {tenantId} · <Link to="/app/tenants">Change</Link>
      </p>
      <PeNotice error={error} busy={busy} message={message} />
      {extra ? <div className="pay-toolbar">{extra}</div> : null}
      {tabs.length > 1 ? <TabBar tabs={tabs} active={active.id} onChange={setTab} /> : null}
      <section className="panel">
        <h2>{active.label}</h2>
        <PeTable
          columns={active.columns}
          rows={rows}
          empty="Nothing returned from Payroll Engine."
          onDelete={active.toPayload ? onDelete : undefined}
        />
      </section>
      {active.fields ? (
        <section className="panel">
          <h2>Add</h2>
          <form className="form" onSubmit={onCreate}>
            {active.fields.map((field) => (
              <label key={field.key}>
                {field.label}
                <input
                  type={field.type || 'text'}
                  required={!!field.required}
                  value={draft[field.key] || ''}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                />
              </label>
            ))}
            <button className="btn btn-primary" type="submit" disabled={busy}>Add</button>
          </form>
        </section>
      ) : null}
    </PePage>
  );
}
